import React, { useContext, useEffect, useState, useRef, useMemo } from 'react'
import { Handle, Position } from '@xyflow/react'
import styles from './custom-node.module.css'
import NodeValueContext from '../../context/node-value.context'
import { Actions, useDispatch, useSelector, Dialogs } from '../../stores/dashboard.store'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { randomColor } from '../../util/util'
import cls from '../../util/cls'

const CustomNodeHandle = ({ idx, position, type, node, isUnresolved = false }) => {
    const { edges } = useContext(NodeValueContext)
    const { colors } = useSelector()

    const edge = useMemo(() => (type === "source") ? edges.find(x => x?.sourceHandle === node?.id) : edges.find(x => x?.targetHandle === node?.id), [
        edges, type, node?.id,
    ])

    const color = useMemo(() => Object.keys(colors || {}).length === 0 ? 'rgb(255,255,255)' : colors[node?.type], [colors, node])

    if (!node?.use_as_edge) {
        return null
    }

    return (
        <div style={{ display: 'flex' }}>
            <div className={styles.simpleLabel} style={{ marginBottom: 5 }}>{node?.name}</div>
            <Handle
                type={type}
                id={node?.id}
                position={position}
                style={{
                    background: !!edge ? edge?.style?.stroke : '#555',
                    transition: 'background 0.8s ease', top: (idx * 18) + (50),
                    border: `1px solid rgb(${color[0]},${color[1]},${color[2]})`,
                    boxShadow: isUnresolved ? `rgb(239, 57, 57) 0px 0px 0px 3px` : undefined,
                }}
            />
        </div>
    )
}

const CustomNode = ({ data, id }) => {
    const { activeNodeId, setActiveNodeId, edges, activeNodeEditMode } = useContext(NodeValueContext)
    const { compileStatus, customNodeNames, tasks, activeProject, loadingProjectId } = useSelector()

    const inputRef = useRef(null)
    const dispatch = useDispatch()

    const [tempName, setTempName] = useState(customNodeNames[id] || data?.name)

    useEffect(() => {
        if (inputRef.current && activeNodeId === id) {
            inputRef.current.focus()
            setTempName(customNodeNames[id] || data?.name)
        }
    }, [activeNodeId, id])

    const onNodeInfoClick = () => {
        dispatch(Actions.SetApplicationDialog, {
            dialog: Dialogs.NodeInformation,
            data: data,
            visible: true,
        })
    }

    const isRunning = useMemo(() => !!tasks[activeProject?.id] || loadingProjectId === activeProject?.id, [tasks, loadingProjectId])

    const updateName = ({ target: { value } }) => {
        setTempName(value)
        dispatch(Actions.SetCustomNodeName, { id, name: value })
    }

    const unResolvedEdges = useMemo(() => data?.inputs.filter(x => x?.use_as_edge && edges?.find(j => j?.targetHandle === x?.id) === undefined), [edges, data?.inputs])
    useEffect(() => {
        dispatch(Actions.SetUnfulfilledNodes, { id: id, value: unResolvedEdges })
    }, [unResolvedEdges])

    const hasError = compileStatus?.brokenProjects?.includes(data?.projectId)

    const color = useMemo(() => data?.color || randomColor(), [])

    return (
        <div
            className={cls(hasError ? styles.error : styles.node, activeNodeId === id ? styles.activeNode : undefined)}
        >
            <div
                className={styles.header}
                style={{
                    background: `rgba(${color[0]}, ${color[1]}, ${color[2]}, 1)`,
                }}
            >
                <HelpOutlineIcon onClick={onNodeInfoClick} style={{ color: 'white', fontSize: 10}} />

                <div
                    className={styles.headerName}
                    onClick={() => isRunning ? undefined : setActiveNodeId(id, true)}
                >
                    <div className={styles.name}>
                        {({
                            [true]: customNodeNames[id] || data?.name,
                            [activeNodeId === id && activeNodeEditMode === true]: <input ref={inputRef} className={styles.nameInput} value={tempName} onChange={updateName} />,
                        }).true}
                    </div>
                    {
                        !(activeNodeId === id && activeNodeEditMode === true) && <div className={styles.description}>{data?.description}</div>
                    }
                </div>
            </div>
            <div
                className={styles.nodeInner}
                onClick={() => setActiveNodeId(id)}
            >
                <div style={{ marginTop: '3px' }}>
                    {data?.inputs?.map((x, idx) => (
                        <CustomNodeHandle node={x} type="target" idx={idx} position={Position.Left} isUnresolved={edges?.find(j => j?.targetHandle === x?.id) === undefined} />
                    ))}
                </div>
                <div>
                    {data?.outputs?.map((x, idx) => (
                        <CustomNodeHandle node={x} type="source" idx={idx} position={Position.Right} />
                    ))}
                </div>
            </div>
        </div>
    )
}


export default CustomNode